import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MultiSelectOptionsInput } from "@/components/customFields/MultiSelectOptionsInput";
import type { CustomFieldType } from "@/lib/customFieldTypes";

/** Sentinel for the "no default" option, prefixed with a null byte so it cannot collide with a user-defined option. */
const NO_DEFAULT_SENTINEL = "\0__no_default__";

/** Props for the CustomFieldDefaultValueInput component. */
interface CustomFieldDefaultValueInputProps {
  fieldType: CustomFieldType;
  options: string[];
  value: string | number | string[] | undefined;
  onChange: (value: string | number | string[] | undefined) => void;
  disabled: boolean;
}

/**
 * Type-aware input for the default value of a custom field definition.
 * Renders nothing for user and attachment fields, which have no meaningful default.
 * @param fieldType - The field's editor type
 * @param options - The option list for select and multi-select fields
 * @param value - The current default value, or undefined when unset
 * @param onChange - Callback with the new default value, undefined to clear it
 * @param disabled - Whether editing is disabled
 */
export function CustomFieldDefaultValueInput({
  fieldType,
  options,
  value,
  onChange,
  disabled,
}: CustomFieldDefaultValueInputProps) {
  if (
    fieldType === "user" ||
    fieldType === "multiUser" ||
    fieldType === "attachments"
  ) {
    return null;
  }

  if (fieldType === "select") {
    const selected = typeof value === "string" ? value : "";
    return (
      <Select
        value={selected}
        onValueChange={(next) =>
          onChange(next === NO_DEFAULT_SENTINEL ? undefined : next)
        }
        disabled={disabled || options.length === 0}
      >
        <SelectTrigger aria-label="Default value" className="h-7 text-xs">
          <SelectValue placeholder="No default" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem
            value={NO_DEFAULT_SENTINEL}
            className="text-muted-foreground italic"
          >
            No default
          </SelectItem>
          {options.map((option) => (
            <SelectItem key={option} value={option}>
              {option}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  if (fieldType === "multiSelect") {
    return (
      <MultiSelectOptionsInput
        options={options}
        selectedValues={Array.isArray(value) ? value : []}
        onValuesChange={(values) =>
          onChange(values.length > 0 ? values : undefined)
        }
        disabled={disabled || options.length === 0}
        size="sm"
      />
    );
  }

  const isNumeric = fieldType === "number" || fieldType === "percentage";
  const inputType = isNumeric
    ? "number"
    : fieldType === "date"
      ? "date"
      : "text";

  return (
    <Input
      aria-label="Default value"
      type={inputType}
      value={value === undefined ? "" : String(value)}
      onChange={(event) =>
        onChange(parseDefaultValue(event.target.value, fieldType))
      }
      disabled={disabled}
      placeholder="No default"
      min={fieldType === "percentage" ? 0 : undefined}
      max={fieldType === "percentage" ? 100 : undefined}
      className="h-7 text-xs"
    />
  );
}

/**
 * Parses raw input text into a default value matching the field's type.
 * @param raw - The raw input value
 * @param fieldType - The field's editor type
 * @returns The parsed default value, or undefined when the input is blank or unparseable
 */
function parseDefaultValue(
  raw: string,
  fieldType: CustomFieldType,
): string | number | undefined {
  if (raw === "") {
    return undefined;
  }
  if (fieldType !== "number" && fieldType !== "percentage") {
    return raw;
  }
  const parsed = Number(raw);
  if (Number.isNaN(parsed)) {
    return undefined;
  }
  if (fieldType === "percentage") {
    return Math.min(100, Math.max(0, parsed));
  }
  return parsed;
}
